import type { GrantRole, OrgUser, ShareTeam } from "@common/components/share/types"
import { getDisplayName, teamMemberCountLabel } from "@common/components/share/utils"

export type ShareableResourceKind = "workspace" | "skill" | "flow" | "prompt" | "file" | "clause" | "chat"

export type ShareGrantee = { kind: "user"; user: OrgUser } | { kind: "team"; team: ShareTeam }

export function userGrantee(user: OrgUser): ShareGrantee {
  return { kind: "user", user }
}

export function teamGrantee(team: ShareTeam): ShareGrantee {
  return { kind: "team", team }
}

export function userGranteeKey(userId: string) {
  return `user:${userId}`
}

export function teamGranteeKey(teamId: string) {
  return `team:${teamId}`
}

export function granteeKey(grantee: ShareGrantee) {
  return grantee.kind === "user" ? userGranteeKey(grantee.user.userId) : teamGranteeKey(grantee.team.teamId)
}

export function granteeName(grantee: ShareGrantee) {
  return grantee.kind === "user" ? getDisplayName(grantee.user) : grantee.team.name
}

export function granteeDetail(grantee: ShareGrantee) {
  return grantee.kind === "user" ? grantee.user.email : teamMemberCountLabel(grantee.team.memberCount)
}

export function granteeRole(grantee: ShareGrantee): GrantRole {
  return (grantee.kind === "user" ? grantee.user.role : grantee.team.role) ?? "viewer"
}

function granteeShortName(grantee: ShareGrantee) {
  return grantee.kind === "user" ? getDisplayName(grantee.user).split(" ")[0] : grantee.team.name
}

const WORKSPACE_SHARE_DESCRIPTION =
  "Invite collaborators to this workspace. They will have access to all its artifacts and flows. Your chats remain private."

const CLAUSE_SHARE_TITLE_MAX_CHARS = 60

export function clauseShareTitle(contentText: string) {
  return contentText.length > CLAUSE_SHARE_TITLE_MAX_CHARS ? `${contentText.slice(0, CLAUSE_SHARE_TITLE_MAX_CHARS).trimEnd()}…` : contentText
}

export function shareDialogTitle(resourceName: string) {
  return `Share “${resourceName}”`
}

export function shareDialogDescription(resourceKind: ShareableResourceKind) {
  if (resourceKind === "workspace") return WORKSPACE_SHARE_DESCRIPTION
  return `Invite collaborators to this ${resourceKind}.`
}

export const GRANT_ROLE_LABELS: Record<GrantRole, string> = {
  editor: "Editor",
  viewer: "Viewer",
}

export function grantRoleDescription(role: GrantRole, resourceKind: ShareableResourceKind) {
  if (role === "editor") {
    return `Full access to view, edit, delete, and share this ${resourceKind} and its contents with users or teams.`
  }
  return `Read-only access to view and explore this ${resourceKind} and its contents.`
}

const namesInSentence = new Intl.ListFormat("en", { style: "long", type: "conjunction" })

function countOthers(count: number) {
  return `${count} ${count === 1 ? "other" : "others"}`
}

export const GRANTEES_SHOWN_AS_AVATARS = 5

export function granteesWithAccessSummary(grantees: ShareGrantee[], currentUserId: string | undefined) {
  const hiddenGranteeCount = grantees.length - GRANTEES_SHOWN_AS_AVATARS
  const onlyGrantee = grantees.length === 1 ? grantees[0] : undefined
  if (onlyGrantee?.kind === "user" && onlyGrantee.user.userId === currentUserId) return "Only you have access"
  if (hiddenGranteeCount > 0) return `+ ${countOthers(hiddenGranteeCount)}`
  return ""
}

const SELECTED_TEAM_NAMES_SHOWN = 2

export function selectedTeamsSummary(teams: ShareTeam[]) {
  const shownNames = teams.slice(0, SELECTED_TEAM_NAMES_SHOWN).map((team) => team.name)
  const hiddenCount = teams.length - shownNames.length
  const others = hiddenCount > 0 ? [`${hiddenCount} other ${hiddenCount === 1 ? "team" : "teams"}`] : []
  return namesInSentence.format([...shownNames, ...others])
}

export function teamInviteDetail(team: ShareTeam, role: GrantRole) {
  return `${teamMemberCountLabel(team.memberCount)} can ${role === "editor" ? "edit" : "view"}`
}

const INVITED_NAMES_SHOWN = 3

export function sharedWithConfirmation(resourceName: string, invitedGrantees: ShareGrantee[]) {
  const shownNames = invitedGrantees.slice(0, INVITED_NAMES_SHOWN).map(granteeShortName)
  const hiddenCount = invitedGrantees.length - shownNames.length
  const others = hiddenCount > 0 ? [countOthers(hiddenCount)] : []
  return `“${resourceName}” shared with ${namesInSentence.format([...shownNames, ...others])}`
}

function quotedTeamNames(teams: ShareTeam[]) {
  return namesInSentence.format(teams.map((team) => `“${team.name}”`))
}

export function teamInviteQuestion(teams: ShareTeam[], resourceName: string, role: GrantRole) {
  return `Invite ${quotedTeamNames(teams)} to ${role === "editor" ? "edit" : "view"} “${resourceName}”?`
}

export function teamInviteConsequence(teams: ShareTeam[], resourceKind: ShareableResourceKind, role: GrantRole) {
  const memberCount = teams.reduce((total, team) => total + team.memberCount, 0)
  const access = role === "editor"
    ? `full access to view, edit, delete, and share this ${resourceKind} and its contents with other users or teams`
    : `read-only access to view and explore this ${resourceKind} and its contents`
  return `This will grant ${memberCount} ${memberCount === 1 ? "user" : "users"} on ${quotedTeamNames(teams)} ${access}.`
}

export function removeCollaboratorQuestion(grantee: ShareGrantee, resourceName: string, resourceKind: ShareableResourceKind) {
  const whose = grantee.kind === "team" ? "its members'" : "their"
  return `Remove ${granteeName(grantee)} from “${resourceName}”? This will immediately remove ${whose} access to this ${resourceKind} and all of its contents.`
}

export function removedCollaboratorConfirmation(grantee: ShareGrantee) {
  return `Successfully removed ${granteeName(grantee)}`
}

const GRANT_ROLE_LABELS_WITH_ARTICLE: Record<GrantRole, string> = {
  editor: "an Editor",
  viewer: "a Viewer",
}

export function permissionChangedConfirmation(grantee: ShareGrantee, role: GrantRole) {
  return `${granteeName(grantee)} is now ${GRANT_ROLE_LABELS_WITH_ARTICLE[role]}`
}
